import { API_BASE } from '@/env';
import { apiFetch } from '@/auth/apiClient';

// ── Types ──────────────────────────────────────────────────────

export interface Strategy {
  id: string;
  name: string;
  description: string;
  scannerId: string;
  direction: 'Long' | 'Short';
  entryFilters: ScannerFilter[];
  sizing: PositionSizing;
  exits: ExitRules;
  cooldownSeconds: number;
  maxConcurrentTrades: number;
  // Seconds an entry order may sit unfilled before it's cancelled as illiquid.
  // null = the server's 60s default. Required-but-nullable: the server always
  // serializes the field (int? with a default), so it's never absent on a GET.
  entryFillTimeoutSeconds: number | null;
  // 'Market' (default) or 'Limit'. Limit entries cap slippage: the order
  // fills only at/through the signal price (+offset), else the entry-fill
  // timeout cancels it. Server enum, always serialized.
  entryOrderType?: 'Market' | 'Limit';
  // Marketable buffer (basis points) added to the signal price for limit
  // entries — buy fills up to price*(1+bps), sell down to price*(1-bps).
  entryLimitOffsetBps?: number;
  // Adverse slippage (bps/side) the backtest applies to market fills so
  // simulated PnL reflects real execution cost. Limit entries are exempt.
  backtestSlippageBps?: number;
  // Entry extension guard: reject an entry when price is more than this % above
  // the 50-day SMA (don't chase a name that's already run). 0 disables. Default 15.
  entryMaxExtensionAboveSma50Pct?: number;
  regularHoursOnly: boolean;
  enabled: boolean;
  createdAt: string;
  lastUpdatedAt: string | null;
  // Optional ET entry-window bounds (TimeOnly? on the server). When present,
  // serialized as "HH:mm:ss" strings (e.g. "09:30:00").
  entryStartTimeEt?: string | null;
  entryEndTimeEt?: string | null;
  // Which asset family + account the rule trades. The server always serializes
  // AssetClass (enum, default "Equity"); TradingEnvironment is "Paper" (default)
  // or "Live". Read by LiveAutomationPanel to roster + show armed state — absent
  // from the type would silently infer `any` under strict:false and let a
  // serializer drift default every rule to Equity/Paper unnoticed.
  assetClass: string;
  tradingEnvironment?: 'Paper' | 'Live';
  // Which paper account this strategy trades — its own pot of capital, so two strategies
  // can't spend the same balance or trip each other's buying-power circuit breaker.
  // null = the shared per-user paper account. On UPDATE the server treats undefined/null as
  // "keep the current binding" and an EMPTY STRING as an explicit unbind, because the edit
  // form does a full-object PUT and an omitted field would otherwise silently unbind.
  paperAccountId?: string | null;
  // Polymorphic asset-class config (server: AssetRuleConfig with a $type
  // discriminator). Forex carries lots/pips/sessions; equity is a placeholder.
  ruleConfig?: RuleConfig | null;
  // Opening-range-breakout entry trigger. null/absent = the scanner's rising edge is the
  // trigger (the default). When set, the range break triggers and the scanner is a filter.
  orbEntry?: OrbEntryTrigger | null;
  // Max entries per symbol per ET session. 0 = unlimited.
  maxEntriesPerSymbolPerDay?: number;
  // Whether the backtest resolves stop/target hits against each bar's high/low (true, default)
  // or only its close (the legacy model — set false to reproduce a run recorded before the fix).
  backtestIntrabarExits?: boolean;
  // Reject an entry whose stop would sit closer than this % of price. 0/absent = off.
  minStopDistancePct?: number;
  // Backtest commission, ROUND TRIP: dollars per unit (share/contract) and a flat per-trade
  // ticket. Slippage is proportional to price; commission is proportional to SIZE, so a
  // bps-only model cannot express it. 0/absent = off.
  backtestCommissionPerUnit?: number;
  backtestCommissionPerTrade?: number;
}

/// Opening-range-breakout entry trigger — mirrors the server's OrbEntryTrigger.
export interface OrbEntryTrigger {
  // How far past the range edge price must go, in basis points OF THE EDGE. 0 = a bare touch.
  breakoutMarginBps: number;
  // true (default) = the bar must CLOSE beyond edge+margin; false = an intrabar touch qualifies.
  requireCloseBeyond: boolean;
  // true (default) = only break a range whose window has already closed.
  requireCompleteRange: boolean;
}

/// Polymorphic per-asset rule config — mirrors the server's AssetRuleConfig
/// ($type discriminator: "Equity" | "Forex").
export type RuleConfig = EquityRuleConfig | ForexRuleConfig;

export interface EquityRuleConfig {
  $type: 'Equity';
}

export interface ForexRuleConfig {
  $type: 'Forex';
  /** Position size in lots (1.0 = standard, 0.1 = mini, 0.01 = micro). */
  lotSize: number;
  /** Stop distance in pips; null → fall back to the root ATR/dollar stop. */
  stopLossPips?: number | null;
  /** Take-profit distance in pips; null → use the root take-profit (or none). */
  takeProfitPips?: number | null;
  /** FXCM swap-roll blackout start, ET, "HH:mm:ss". null = no blackout. */
  rolloverBlackoutStartEt?: string | null;
  /** Blackout window length, "hh:mm:ss". */
  rolloverBlackoutDuration?: string;
  /** Only enter during the London+NY liquid overlap (08:00–17:00 ET). */
  liquidSessionsOnly: boolean;
}

export interface ScannerFilter {
  field: string;
  operator: string;
  value: any;
  value2?: any;
}

export interface PositionSizing {
  mode: 'FixedShares' | 'FixedDollar' | 'RiskPercent' | 'PercentEquity';
  value: number;
  accountEquity: number;
  maxNotional: number;
}

export interface ExitRules {
  stopLoss: StopRule | null;
  takeProfit: StopRule | null;
  trailing: TrailingStopRule | null;
  breakEvenAfterRMultiple: number;
  maxHoldMinutes: number;
  /** Optional ET hard session-end exit (TimeOnly? server-side, "HH:mm:ss"). */
  sessionEndTimeEt?: string | null;
  /** Optional max-loss-percent circuit breaker on the position. */
  maxLossPercent?: number | null;
  /** Opening-range window as ET wall clock. Null start = 09:30; null end falls back
   *  to openRangeMinutes (and to "the first bar is the range" when that is 0). */
  openRangeMinutes?: number;
  openRangeStartEt?: string | null;
  openRangeEndEt?: string | null;
}

export interface StopRule {
  /** EntryCandle is BACKTEST-ONLY and valid only as a stop-loss — the live path
   *  carries no signal bar, and it produces no take-profit or trailing distance. */
  mode: 'Dollar' | 'Percent' | 'AtrMultiple' | 'RMultiple' | 'RangeMultiple' | 'SwingStructure' | 'EntryCandle';
  value: number;
}

export interface TrailingStopRule {
  mode: 'Dollar' | 'Percent' | 'AtrMultiple' | 'RMultiple';
  value: number;
  activationPercent: number;
  /** Which reference price drives the Percent-mode trail distance.
   *   LastPrice (default, legacy) — distance recomputed each bar; tightens on retrace.
   *   MaxFavorable (classic)      — distance frozen at the favored extreme; "X% behind the high".
   * Only meaningful for mode==='Percent'. */
  distanceReference?: 'LastPrice' | 'MaxFavorable';
}

/// Lightweight version-history row from GET /api/Strategy/{id}/specs.
/// Provenance only — fetch the full StrategySpec per version for the diff view.
export interface StrategySpecSummary {
  version: number;
  createdAt: string;
  createdByUserId: string | null;
}

/// Immutable snapshot of a strategy's trade-affecting fields at one edit,
/// from GET /api/Strategy/{id}/specs/{version}. Mirrors the backend
/// StrategySpec model.
export interface StrategySpec {
  id: string;
  strategyId: string;
  version: number;
  scannerId: string;
  direction: 'Long' | 'Short';
  entryFilters: ScannerFilter[];
  sizing: PositionSizing;
  exits: ExitRules;
  cooldownSeconds: number;
  maxConcurrentTrades: number;
  entryFillTimeoutSeconds: number | null;
  entryOrderType?: 'Market' | 'Limit';
  entryLimitOffsetBps?: number;
  backtestSlippageBps?: number;
  // Entry extension guard: reject an entry when price is more than this % above
  // the 50-day SMA (don't chase a name that's already run). 0 disables. Default 15.
  entryMaxExtensionAboveSma50Pct?: number;
  regularHoursOnly: boolean;
  entryStartTimeEt: string | null;
  entryEndTimeEt: string | null;
  lunchBlackoutStartEt: string | null;
  lunchBlackoutEndEt: string | null;
  assetClass: string;
  // 'Paper' (default) | 'Live' — which account the rule's live instance trades.
  tradingEnvironment?: string;
  ruleConfig: unknown | null;
  // Entry/exit controls the version diff has to show, or two versions that traded
  // differently render as identical. Absent on rows written before these existed.
  orbEntry?: OrbEntryTrigger | null;
  maxEntriesPerSymbolPerDay?: number;
  minStopDistancePct?: number;
  backtestCommissionPerUnit?: number;
  backtestCommissionPerTrade?: number;
  // false on legacy rows — those versions were produced by the close-only exit engine.
  backtestIntrabarExits?: boolean;
  createdAt: string;
  createdByUserId: string | null;
}

export interface StrategyTrade {
  id: string;
  ruleId: string;
  scannerId: string;
  sessionId: string | null;
  symbol: string;
  direction: 'Long' | 'Short';
  /** EntryPending = broker accepted, fill unconfirmed (no position yet);
   * ExitPending = exit in flight, position still owned. */
  status: 'Pending' | 'Open' | 'Closed' | 'Cancelled' | 'EntryPending' | 'ExitPending';
  /** Write-once at open (denormalized from the rule). Null on pre-field legacy trades —
   * chart callers fall back to the rule/snapshot value, never overwrite a present one:
   * editing a strategy's asset class must not re-render its old trades' history. */
  assetClass?: string | null;
  entryTime: string;
  entryPrice: number;
  quantity: number;
  entryReason: string;
  initialStopPrice: number;
  initialTakeProfitPrice: number | null;
  initialRiskPerShare: number;
  currentStopPrice: number;
  currentTakeProfitPrice: number | null;
  maxFavorablePrice: number;
  maxAdversePrice: number;
  lastPrice: number;
  exitTime: string | null;
  exitPrice: number | null;
  exitReason: string | null;
  realizedPnL: number;
  realizedPnLPercent: number;
  realizedRMultiple: number;
  holdingSeconds: number;
  maxFavorableExcursion: number;
  maxAdverseExcursion: number;
  events: TradeEvent[];
}

/** Statuses that own (or owned) a real position — the only trades a price chart can
 * honestly render. Pending/EntryPending have no confirmed fill; Cancelled never became
 * a position — for any of those the dialog would mark the decision price as an entry
 * and treat "no exitTime" as open-until-today. */
export function tradeChartable(t: Pick<StrategyTrade, 'status'>): boolean {
  return t.status === 'Open' || t.status === 'Closed' || t.status === 'ExitPending';
}

export interface TradeEvent {
  time: string;
  type: string;
  price: number;
  stopPrice: number | null;
  notes: string | null;
  /** Structured signal snapshot captured at Entry from the scanner detection
   *  (RsRating, distance from SMAs, 52w high/low, RelVol, change%, RSI2, ATR, …).
   *  Keyed by metric name → value. Present on Entry events; null otherwise.
   *  Backend dictionary keys are not camelCased, so render key-agnostically. */
  signal?: Record<string, number> | null;
}

export interface RuleStatistics {
  ruleId: string;
  sessionId: string | null;
  totalTrades: number;
  openTrades: number;
  winners: number;
  losers: number;
  breakeven: number;
  winRate: number;
  totalPnL: number;
  totalPnLPercent: number;
  avgWin: number;
  avgLoss: number;
  largestWin: number;
  largestLoss: number;
  expectancy: number;
  profitFactor: number;
  avgRMultiple: number;
  maxDrawdown: number;
  maxDrawdownPercent: number;
  avgHoldingSeconds: number;
  // Range/exit-resolution metrics (present in the slim history payload too).
  pctResolvedByExit?: number | null;
  avgOpeningRangeWidth?: number | null;
  // Extended MT5-style metrics.
  grossProfit?: number | null;
  grossLoss?: number | null;
  sharpeRatio?: number | null;
  recoveryFactor?: number | null;
  lrCorrelation?: number | null;
  maxConsecutiveWins?: number | null;
  maxConsecutiveWinsProfit?: number | null;
  maxConsecutiveLosses?: number | null;
  maxConsecutiveLossesAmount?: number | null;
  longTrades?: number | null;
  longWins?: number | null;
  longPnL?: number | null;
  shortTrades?: number | null;
  shortWins?: number | null;
  shortPnL?: number | null;
  equityCurve: EquityPoint[];
  bestTrades: StrategyTrade[];
  worstTrades: StrategyTrade[];
  computedAt: string;
}

export interface EquityPoint {
  time: string;
  equity: number;
  cumulativePnL: number;
  tradeId: string;
  symbol: string;
}

// Renamed from `Scenario` in slice 4 (backend) — kept the TS alias for
// existing UI consumers that import `Scenario`. The wire format matches the
// new `Run` shape: strategyId, strategySnapshot, endedAt, nested backtest.*.
export interface Scenario {
  id: string;
  name: string;
  strategyId: string;
  scannerId: string;
  symbols: string[];
  mode: 'Backtest' | 'RealTime';
  status: 'Pending' | 'Running' | 'Completed' | 'Failed' | 'Stopped';
  createdAt: string;
  startedAt: string | null;
  endedAt: string | null;
  error: string | null;
  strategySnapshot: Strategy | null;
  statistics: RuleStatistics | null;
  trades: StrategyTrade[];
  backtest: { startDate: string; endDate: string; symbolListId: string | null; backtestSessionId: string | null } | null;
}

/// Lightweight scenario projection returned by GET /api/Scenario (the list endpoint).
/// Same fields as Scenario except the trades array is replaced with a tradeCount
/// — saves bandwidth (5MB+ trade arrays were being shipped per scenario unnecessarily).
/// Use getScenario(id) to fetch the full Scenario including trades.
export interface ScenarioSummary {
  id: string;
  name: string;
  strategyId: string;
  scannerId: string;
  symbols: string[];
  mode: 'Backtest' | 'RealTime';
  status: 'Pending' | 'Running' | 'Completed' | 'Failed' | 'Stopped';
  createdAt: string;
  startedAt: string | null;
  endedAt: string | null;
  error: string | null;
  strategySnapshot: Strategy | null;
  statistics: RuleStatistics | null;
  tradeCount: number;
  backtest: { startDate: string; endDate: string; symbolListId: string | null; backtestSessionId: string | null } | null;
}

/// An expiring, revocable scenario share link — returned by createScenarioShare
/// and listScenarioShares. The token is the credential; the shareable URL is
/// built from it client-side (#/dashboard/trade-viewer?key=<token>).
export interface ScenarioShareLink {
  token: string;
  runId: string;
  createdAt: string;
  expiresAt: string;
}

// ── Backtest Lab (batch) types — mirror AdminBacktestBatchController ──

export interface BacktestBatchItem {
  strategyId: string;
  lookbackDays?: number;
  symbols?: string[] | null;
  endDate?: string | null;
  symbolListId?: string | null;
  /** Replay-granularity override ("1m"/"15m"/"30m"/"1h"/"4h"/"1D"); null = strategy's stored TF. */
  backtestTimeframe?: string | null;
  /** Account-size override for sizing experiments; null = strategy's own sizing. */
  accountEquity?: number | null;
  /** Per-trade notional cap override; null = defaults to accountEquity when that is set. */
  maxNotional?: number | null;
}

/// One ranked row. Asset-class-agnostic; diagnostics explain a 0-trade row.
export interface BacktestBatchResultItem {
  runId: string;
  strategyId: string;
  strategyName: string;
  assetClass: string;
  status: string;
  error: string | null;
  tradeCount: number;
  totalPnL: number | null;
  winRate: number | null;
  profitFactor: number | null;
  avgRMultiple: number | null;
  maxDrawdownPercent: number | null;
  /** Effective replay timeframe this run used ("1m"/"15m"/.../"1D"). */
  timeframe: string;
  symbolsDiscovered: number | null;
  barsEmitted: number | null;
  zeroTradeReason: string | null;
  /** % of trades that resolved at a real price level (vs drifted to a forced close). */
  pctResolvedByExit: number | null;
  /** Avg opening-range width ($) across trades that carried one (RangeMultiple/ORB). */
  avgOpeningRangeWidth: number | null;
  /** Quality/risk metrics — rank the scoreboard by curve quality, not just PnL. */
  sharpeRatio?: number | null;
  recoveryFactor?: number | null;
  lrCorrelation?: number | null;
}

/** Owned symbol-list summary for the Backtest Lab universe picker. */
export interface SymbolListSummary {
  id: string;
  name: string;
  symbols: string[];
}

/** One saved ranking row — mirrors BacktestBatchResultItem's headline fields. */
export interface ComparisonRow {
  runId: string;
  strategyId: string;
  strategyName: string;
  assetClass: string;
  timeframe: string;
  status: string;
  tradeCount: number;
  totalPnL: number | null;
  winRate: number | null;
  profitFactor: number | null;
  avgRMultiple: number | null;
  maxDrawdownPercent: number | null;
  pctResolvedByExit: number | null;
  avgOpeningRangeWidth: number | null;
  sharpeRatio?: number | null;
  recoveryFactor?: number | null;
  lrCorrelation?: number | null;
}

/** A saved Backtest Lab comparison (full — with rows). */
export interface SavedComparison {
  id: string;
  name: string;
  notes?: string | null;
  createdAt: string;
  lookbackDays: number;
  endDate?: string | null;
  symbolListId?: string | null;
  symbolListName?: string | null;
  accountEquity?: number | null;
  timeframes: string[];
  rows: ComparisonRow[];
}

/** List projection — headline only, no rows. */
export interface ComparisonSummary {
  id: string;
  name: string;
  notes: string | null;
  createdAt: string;
  rowCount: number;
  symbolListName: string | null;
  lookbackDays: number;
  accountEquity: number | null;
}

/// An expiring, revocable comparison share link — the token is the credential;
/// the public URL is built client-side (#/shared-comparison?key=<token>).
export interface ComparisonShareLink {
  token: string;
  comparisonId: string;
  createdAt: string;
  expiresAt: string;
}

// ── Service ────────────────────────────────────────────────────

class AutomationApiService {
  // ── Rules ──

  async getRules(): Promise<Strategy[]> {
    // Back-compat shim: returns up to 100 rules. New callers should use getRulesPage.
    const page = await this.getRulesPage({ pageSize: 100 });
    return page.items;
  }

  async getRulesPage(opts: {
    page?: number;
    pageSize?: number;
    sortBy?: RuleSortBy;
    sortDir?: SortDir;
    /** Case-insensitive substring match on the strategy name. */
    search?: string;
    /** true = active only, false = inactive only, undefined = both. */
    enabled?: boolean;
    /** 'Equity' | 'Forex' | 'Crypto'. */
    assetClass?: string;
  } = {}): Promise<RulesPage> {
    const params = new URLSearchParams();
    if (opts.page != null) params.set('page', String(opts.page));
    if (opts.pageSize != null) params.set('pageSize', String(opts.pageSize));
    if (opts.sortBy) params.set('sortBy', opts.sortBy);
    if (opts.sortDir) params.set('sortDir', opts.sortDir);
    // Filters go to the SERVER so they apply across every page, not just the rows
    // already loaded — a client-side status filter would hide matching strategies
    // sitting on another page with nothing on screen to say so.
    if (opts.search?.trim()) params.set('search', opts.search.trim());
    if (opts.enabled != null) params.set('enabled', String(opts.enabled));
    if (opts.assetClass) params.set('assetClass', opts.assetClass);
    const qs = params.toString();
    const url = qs ? `${API_BASE}/api/Strategy?${qs}` : `${API_BASE}/api/Strategy`;
    const res = await apiFetch(url);
    if (!res.ok) throw new Error(await res.text());
    const body = await res.json();
    // Server may return either the new envelope or a flat array (older deploy).
    if (Array.isArray(body)) {
      return {
        items: body as Strategy[],
        total: body.length,
        page: opts.page ?? 1,
        pageSize: opts.pageSize ?? body.length,
      };
    }
    return body as RulesPage;
  }

  async getRule(ruleId: string): Promise<Strategy> {
    const res = await apiFetch(`${API_BASE}/api/Strategy/${ruleId}`);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }

  async createRule(rule: Partial<Strategy>): Promise<Strategy> {
    const res = await apiFetch(`${API_BASE}/api/Strategy`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(rule),
    });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }

  async updateRule(ruleId: string, rule: Partial<Strategy>): Promise<Strategy> {
    const res = await apiFetch(`${API_BASE}/api/Strategy/${ruleId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(rule),
    });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }

  async deleteRule(ruleId: string): Promise<void> {
    const res = await apiFetch(`${API_BASE}/api/Strategy/${ruleId}`, { method: 'DELETE' });
    if (!res.ok) throw new Error(await res.text());
  }

  async enableRule(ruleId: string): Promise<void> {
    const res = await apiFetch(`${API_BASE}/api/Strategy/${ruleId}/enable`, { method: 'POST' });
    if (!res.ok) throw new Error(await res.text());
  }

  async disableRule(ruleId: string): Promise<void> {
    const res = await apiFetch(`${API_BASE}/api/Strategy/${ruleId}/disable`, { method: 'POST' });
    if (!res.ok) throw new Error(await res.text());
  }

  /** Arm (enabled=true) or disarm (false) a rule for REAL-MONEY live automation.
   *  Arm sets TradingEnvironment=Live + starts the live run; disarm stops + resets to Paper. */
  async setLiveAutomation(ruleId: string, enabled: boolean): Promise<void> {
    const res = await apiFetch(`${API_BASE}/api/Strategy/${ruleId}/live-automation`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ enabled }),
    });
    if (!res.ok) {
      // Unwrap {error} so the arm/disarm failure reads as a sentence, not JSON.
      const text = await res.text();
      let msg = text || `Request failed (${res.status})`;
      try { msg = JSON.parse(text)?.error ?? msg; } catch { /* not JSON */ }
      throw new Error(msg);
    }
  }

  async getRuleStatistics(ruleId: string): Promise<RuleStatistics> {
    const res = await apiFetch(`${API_BASE}/api/Strategy/${ruleId}/statistics`);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }

  async getRuleTrades(ruleId: string, limit?: number): Promise<StrategyTrade[]> {
    const url = limit
      ? `${API_BASE}/api/Strategy/${ruleId}/trades?limit=${limit}`
      : `${API_BASE}/api/Strategy/${ruleId}/trades`;
    const res = await apiFetch(url);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }

  /// One server-paged, filtered, sorted page of a strategy's PERSISTED trades.
  /// Unlike getRuleTrades (which reads the in-memory performance grain and therefore
  /// only sees trades since the silo last started), this reads the durable documents.
  async getRuleTradesPage(ruleId: string, opts: TradePageQuery = {}): Promise<TradesPage> {
    const qs = buildTradeQuery(opts);
    const res = await apiFetch(`${API_BASE}/api/Strategy/${ruleId}/trades/page?${qs}`);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }

  // ── Spec version history ──

  /// Version history for a strategy, oldest-first. Empty for a pre-versioning
  /// rule (no edits recorded since the slice-B backfill).
  async getSpecVersions(ruleId: string): Promise<StrategySpecSummary[]> {
    const res = await apiFetch(`${API_BASE}/api/Strategy/${ruleId}/specs`);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }

  /// The full immutable spec at a specific version. Throws on 404 (version
  /// doesn't exist for this strategy).
  async getSpecVersion(ruleId: string, version: number): Promise<StrategySpec> {
    const res = await apiFetch(`${API_BASE}/api/Strategy/${ruleId}/specs/${version}`);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }

  // ── Backtest Lab (batch) ──

  /// Run a batch of strategies through the parallel backtest runner. Asset-class-agnostic —
  /// each strategy carries its own AssetClass, so this works for equity, forex, crypto, and any
  /// future class without change. The endpoint caps at 50/call, so callers chunk; this helper
  /// runs ONE chunk. SuperAdmin-only on the server.
  async runBacktestBatch(items: BacktestBatchItem[]): Promise<BacktestBatchResultItem[]> {
    const res = await apiFetch(`${API_BASE}/api/admin/backtest-batch`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(items),
    });
    if (!res.ok) throw new Error(await res.text());
    const body = await res.json();
    return (body.results ?? []) as BacktestBatchResultItem[];
  }

  /// Owned symbol lists — for the Backtest Lab "common universe" picker, so a head-to-head
  /// compares every strategy on the SAME symbols instead of each strategy's own set.
  async getSymbolLists(): Promise<SymbolListSummary[]> {
    const res = await apiFetch(`${API_BASE}/api/SymbolList`);
    if (!res.ok) throw new Error(await res.text());
    const body = await res.json();
    return (body ?? []).map((l: any) => ({ id: l.id, name: l.name, symbols: l.symbols ?? [] }));
  }

  // ── Saved comparisons (the whole head-to-head set) ──
  async listComparisons(): Promise<ComparisonSummary[]> {
    const res = await apiFetch(`${API_BASE}/api/backtest-comparison`);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }

  async getComparison(id: string): Promise<SavedComparison> {
    const res = await apiFetch(`${API_BASE}/api/backtest-comparison/${id}`);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }

  async saveComparison(body: Omit<SavedComparison, 'id' | 'createdAt'>): Promise<SavedComparison> {
    const res = await apiFetch(`${API_BASE}/api/backtest-comparison`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }

  async deleteComparison(id: string): Promise<void> {
    const res = await apiFetch(`${API_BASE}/api/backtest-comparison/${id}`, { method: 'DELETE' });
    if (!res.ok) throw new Error(await res.text());
  }

  /// Resolve a comparison share token to its full saved comparison (incl. rows).
  /// Anonymous-safe — backs the public #/shared-comparison?key=… scoreboard. 404 on
  /// missing/expired token.
  async getComparisonByShare(token: string): Promise<SavedComparison> {
    const res = await apiFetch(`${API_BASE}/api/backtest-comparison/by-share/${encodeURIComponent(token)}`);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }

  async createComparisonShare(id: string, expiresAt: string): Promise<ComparisonShareLink> {
    const res = await apiFetch(`${API_BASE}/api/backtest-comparison/${encodeURIComponent(id)}/share`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ expiresAt }),
    });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }

  async listComparisonShares(id: string): Promise<ComparisonShareLink[]> {
    const res = await apiFetch(`${API_BASE}/api/backtest-comparison/${encodeURIComponent(id)}/shares`);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }

  /// Revoke a comparison share token. 404 (already gone) is treated as success.
  async revokeComparisonShare(id: string, token: string): Promise<void> {
    const res = await apiFetch(
      `${API_BASE}/api/backtest-comparison/${encodeURIComponent(id)}/share/${encodeURIComponent(token)}`,
      { method: 'DELETE' });
    if (res.status === 404) return;
    if (!res.ok) throw new Error(await res.text());
  }

  // ── Scenarios ──

  async runScenario(strategyId: string, lookbackDays: number = 90, symbols?: string[]): Promise<Scenario> {
    const res = await apiFetch(`${API_BASE}/api/Scenario/run`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      // Backend RunScenarioRequest binds `strategyId` (slice 3 rename).
      // Sending `ruleId` here would silently produce 404 — the DTO would
      // bind StrategyId = "" and the orchestrator lookup misses.
      body: JSON.stringify({ strategyId, lookbackDays, symbols }),
    });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }

  /// includeTrades=false → fast summary (stats + equity + best/worst-10; no 5MB trade array).
  /// Load the full trades separately/on-demand via getScenarioTrades(id).
  async getScenario(scenarioId: string, includeTrades = true): Promise<Scenario> {
    const url = includeTrades
      ? `${API_BASE}/api/Scenario/${scenarioId}`
      : `${API_BASE}/api/Scenario/${scenarioId}?includeTrades=false`;
    const res = await apiFetch(url);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }

  /// On-demand full trade list for a run — the heavy drill-down, fetched only when the user opens it.
  async getScenarioTrades(scenarioId: string): Promise<StrategyTrade[]> {
    const res = await apiFetch(`${API_BASE}/api/Scenario/${scenarioId}/trades`);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }

  /// One server-paged, filtered, sorted page of a run's trades. The unpaged sibling
  /// (getScenarioTrades) ships the whole list, which for a large run is megabytes —
  /// and, more importantly, could only be sorted within whatever the client already
  /// holds, which answers a different question than the user asked.
  async getScenarioTradesPage(scenarioId: string, opts: TradePageQuery = {}): Promise<TradesPage> {
    const qs = buildTradeQuery(opts);
    const res = await apiFetch(`${API_BASE}/api/Scenario/${scenarioId}/trades/page?${qs}`);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }

  /// Paged trades behind a share token — the read-only trade viewer needs to page and
  /// sort exactly like the owner's does.
  async getSharedScenarioTradesPage(token: string, opts: TradePageQuery = {}): Promise<TradesPage> {
    const qs = buildTradeQuery(opts);
    const res = await apiFetch(`${API_BASE}/api/Scenario/by-share/${token}/trades/page?${qs}`);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }

  /// Server-side paged + sorted + filtered run history (Backtest Lab History tab). Sort/filter run
  /// in the DB so they apply across the WHOLE set, not just the page. Returns the page + total count.
  /// `asOf` pins the result set to a moment in time. Callers that page with
  /// "load more" should take it from the first response and echo it back on
  /// every subsequent page: offsets are only stable while the underlying set is,
  /// and a batch inserting a run mid-scroll otherwise duplicates one row and
  /// skips another. Callers that jump to a numbered page omit it.
  async listScenariosPaged(p: {
    limit: number; offset: number; sortKey: string; sortDir: 'asc' | 'desc';
    search?: string; completedOnly?: boolean; profitableOnly?: boolean; minTrades?: number; mode?: string;
    asOf?: string;
  }): Promise<{ items: ScenarioSummary[]; total: number; asOf: string }> {
    const qs = new URLSearchParams();
    qs.set('limit', String(p.limit)); qs.set('offset', String(p.offset));
    qs.set('sortKey', p.sortKey); qs.set('sortDir', p.sortDir);
    if (p.mode) qs.set('mode', p.mode);
    if (p.search?.trim()) qs.set('search', p.search.trim());
    if (p.completedOnly) qs.set('completedOnly', 'true');
    if (p.profitableOnly) qs.set('profitableOnly', 'true');
    if (p.minTrades && p.minTrades > 0) qs.set('minTrades', String(p.minTrades));
    if (p.asOf) qs.set('asOf', p.asOf);
    const res = await apiFetch(`${API_BASE}/api/Scenario/page?${qs.toString()}`);
    if (!res.ok) throw new Error(await res.text());
    const body = await res.json();
    return { items: body.items ?? [], total: body.total ?? 0, asOf: body.asOf };
  }

  /// Returns lightweight summaries (no trades array). Fetch the full scenario with
  /// getScenario(id) when needed.
  async listScenarios(limit?: number): Promise<ScenarioSummary[]> {
    const url = limit
      ? `${API_BASE}/api/Scenario?limit=${limit}`
      : `${API_BASE}/api/Scenario`;
    const res = await apiFetch(url);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }

  async deleteScenario(scenarioId: string): Promise<void> {
    const res = await apiFetch(`${API_BASE}/api/Scenario/${scenarioId}`, { method: 'DELETE' });
    if (!res.ok) throw new Error(await res.text());
  }

  // ── Cascading group endpoints (trade-viewer drill-down) ──

  async getScenarioRuns(): Promise<string[]> {
    const res = await apiFetch(`${API_BASE}/api/Scenario/groups/runs`);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }

  async getScenarioEntries(run: string): Promise<string[]> {
    const res = await apiFetch(`${API_BASE}/api/Scenario/groups/entries?run=${encodeURIComponent(run)}`);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }

  async getScenarioMgmts(run: string, entry: string): Promise<string[]> {
    const res = await apiFetch(`${API_BASE}/api/Scenario/groups/mgmts?run=${encodeURIComponent(run)}&entry=${encodeURIComponent(entry)}`);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }

  async getScenarioWindows(run: string, entry: string, mgmt: string): Promise<ScenarioSummary[]> {
    const res = await apiFetch(
      `${API_BASE}/api/Scenario/groups/windows?run=${encodeURIComponent(run)}&entry=${encodeURIComponent(entry)}&mgmt=${encodeURIComponent(mgmt)}`
    );
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }

  // ── Scenario share links (expiring, revocable) ──

  /// Mint a share link for a backtest run. `expiresAt` is an ISO timestamp and
  /// is required — the API has no default lifetime.
  async createScenarioShare(runId: string, expiresAt: string): Promise<ScenarioShareLink> {
    const res = await apiFetch(`${API_BASE}/api/Scenario/${encodeURIComponent(runId)}/share`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ expiresAt }),
    });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }

  /// List the caller's active (non-expired) share links for a run.
  async listScenarioShares(runId: string): Promise<ScenarioShareLink[]> {
    const res = await apiFetch(`${API_BASE}/api/Scenario/${encodeURIComponent(runId)}/shares`);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }

  /// Resolve a share token to its full backtest scenario. Throws on a
  /// missing/expired token (404).
  async getScenarioByShare(token: string): Promise<Scenario> {
    const res = await apiFetch(`${API_BASE}/api/Scenario/by-share/${encodeURIComponent(token)}`);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }

  /// Revoke a share token. A 404 means the link is already gone — treated as
  /// success. Any other non-OK status throws so the caller can surface the
  /// failure and keep the link visible.
  async revokeScenarioShare(runId: string, token: string): Promise<void> {
    const res = await apiFetch(
      `${API_BASE}/api/Scenario/${encodeURIComponent(runId)}/share/${encodeURIComponent(token)}`,
      { method: 'DELETE' });
    if (res.status === 404) return;       // already gone — success
    if (!res.ok) throw new Error(await res.text());
  }

  // ── Paper trading (Alpaca paper account + automation trades) ──

  async getPaperHealth(): Promise<PaperTradingHealth> {
    const res = await apiFetch(`${API_BASE}/api/PaperTrading/health`);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }

  // accountId (Simulated mode only) selects which paper pot to read/trade —
  // omitted = the shared default pot. Broker mode ignores it server-side.
  async getPaperAccount(accountId?: string): Promise<PaperTradingAccount | null> {
    const q = accountId ? `?accountId=${encodeURIComponent(accountId)}` : '';
    const res = await apiFetch(`${API_BASE}/api/PaperTrading/account${q}`);
    if (res.status === 404) return null; // soft empty state — no creds yet
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }

  async getPaperPositions(accountId?: string): Promise<PaperTradingPosition[]> {
    const q = accountId ? `?accountId=${encodeURIComponent(accountId)}` : '';
    const res = await apiFetch(`${API_BASE}/api/PaperTrading/positions${q}`);
    if (res.status === 404) return [];
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }

  /**
   * Force-close a single broker position. Submits a market order opposite
   * to the current position. Returns true on success.
   */
  async closePaperPosition(symbol: string, accountId?: string): Promise<boolean> {
    const q = accountId ? `?accountId=${encodeURIComponent(accountId)}` : '';
    const res = await apiFetch(`${API_BASE}/api/PaperTrading/positions/${encodeURIComponent(symbol)}/close${q}`, {
      method: 'POST',
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(text || `Failed to close ${symbol}`);
    }
    return true;
  }

  /**
   * Force-close ONE managed paper trade by its trade id (routes through the trade
   * grain's ForceClose). Use this where several trades can share a symbol — e.g. the
   * forex automated dashboard — so closing targets the exact trade, not all on a symbol.
   * Throws with the server message on failure (incl. 409 "still open, will retry").
   */
  async closePaperTrade(tradeId: string): Promise<boolean> {
    const res = await apiFetch(`${API_BASE}/api/PaperTrading/trades/${encodeURIComponent(tradeId)}/close`, {
      method: 'POST',
    });
    if (!res.ok) {
      let msg = `Failed to close trade`;
      try { const j = await res.json(); msg = j.error || j.message || msg; } catch { /* non-json */ }
      throw new Error(msg);
    }
    return true;
  }

  async getPaperRecentOrders(limit: number = 50, accountId?: string): Promise<PaperTradingOrder[]> {
    const q = accountId ? `&accountId=${encodeURIComponent(accountId)}` : '';
    const res = await apiFetch(`${API_BASE}/api/PaperTrading/orders/recent?limit=${limit}${q}`);
    if (res.status === 404) return [];
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }

  async getPaperRecentTrades(limit: number = 100, assetClass?: string): Promise<PaperTradingTrade[]> {
    const ac = assetClass ? `&assetClass=${encodeURIComponent(assetClass)}` : '';
    const res = await apiFetch(`${API_BASE}/api/PaperTrading/trades/recent?limit=${limit}${ac}`);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }

  /**
   * Returns null when the user has no broker creds configured (server 404),
   * so the UI can render a "set creds first" empty state instead of an error.
   * Returns [] when creds are set but no trades exist yet.
   */
  async getPaperTradesByRule(assetClass?: string): Promise<PaperTradingByRule[] | null> {
    const ac = assetClass ? `?assetClass=${encodeURIComponent(assetClass)}` : '';
    const res = await apiFetch(`${API_BASE}/api/PaperTrading/trades/by-rule${ac}`);
    if (res.status === 404) return null;
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }

  // ── Paper trading settings (per-user broker creds + kill-switch) ──

  /**
   * Fetch the current user's broker settings. Returns null when the server
   * indicates the user has no creds configured yet (404), so callers can
   * show the empty state without raising an error popup.
   */
  async getPaperSettings(): Promise<PaperTradingSettings | null> {
    const res = await apiFetch(`${API_BASE}/api/PaperTrading/settings`);
    if (res.status === 404) return null;
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }

  /**
   * Save a partial settings update. Empty/missing apiSecret keeps the
   * existing one; empty apiKey is a hard reset (per server contract).
   */
  async savePaperSettings(partial: PaperTradingSettingsUpdate): Promise<PaperTradingSettings> {
    const res = await apiFetch(`${API_BASE}/api/PaperTrading/settings`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(partial),
    });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }

  /**
   * @deprecated Use getPaperSettings(). Kept so older callers (none currently
   * outside this file) don't break — returns the same shape widened to
   * PaperTradingSettings | null.
   */
  async getPaperConfig(): Promise<PaperTradingSettings | null> {
    return this.getPaperSettings();
  }

  /**
   * @deprecated Master kill-switch — flips paperTradingEnabled via the new
   * /settings endpoint. Kept named the same so existing callers keep working.
   */
  async setPaperConfig(paperTradingEnabled: boolean): Promise<PaperTradingSettings> {
    return this.savePaperSettings({ paperTradingEnabled });
  }

  // ── System-wide master kill switch (SuperAdmin only) ──

  /**
   * Read the system-wide master switch state. Backend gates this endpoint to
   * SuperAdmin; non-admin callers will see 403 here.
   */
  async getSystemPaperSettings(): Promise<SystemPaperTradingSettings> {
    const res = await apiFetch(`${API_BASE}/api/PaperTrading/system-settings`);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }

  /**
   * Flip the master switch. SuperAdmin only — when off, NO user's paper
   * trades will fire regardless of their per-user toggle.
   */
  async setSystemPaperSettings(masterEnabled: boolean): Promise<SystemPaperTradingSettings> {
    const res = await apiFetch(`${API_BASE}/api/PaperTrading/system-settings`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ masterEnabled }),
    });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }
}

// ── Rule list pagination ──

export type RuleSortBy = 'name' | 'createdAt' | 'updatedAt' | 'enabled';

/** Sort keys the trade-list endpoints accept. Must stay in step with the server's
 *  allow-list: an unknown key is rejected there rather than silently defaulted. */
export type TradeSortBy =
  | 'symbol' | 'direction' | 'status' | 'entryTime' | 'exitTime' | 'entryPrice'
  | 'exitPrice' | 'quantity' | 'realizedPnL' | 'realizedPnLPercent'
  | 'realizedRMultiple' | 'holdingSeconds' | 'exitReason';

export interface TradePageQuery {
  page?: number;
  pageSize?: number;
  sortBy?: TradeSortBy;
  sortDir?: SortDir;
  /** Matches symbol, entry reason or exit reason. */
  search?: string;
  direction?: 'Long' | 'Short';
  status?: string;
  /** 'win' | 'loss' | 'breakeven' — closed trades only; an open trade has no outcome yet. */
  outcome?: 'win' | 'loss' | 'breakeven';
  symbol?: string;
}

export interface TradesPage {
  items: StrategyTrade[];
  /** Rows matching the filters across ALL pages, not just this one. */
  total: number;
  page: number;
  pageSize: number;
}

function buildTradeQuery(opts: TradePageQuery): string {
  const qs = new URLSearchParams();
  if (opts.page != null) qs.set('page', String(opts.page));
  if (opts.pageSize != null) qs.set('pageSize', String(opts.pageSize));
  if (opts.sortBy) qs.set('sortBy', opts.sortBy);
  if (opts.sortDir) qs.set('sortDir', opts.sortDir);
  if (opts.search?.trim()) qs.set('search', opts.search.trim());
  if (opts.direction) qs.set('direction', opts.direction);
  if (opts.status) qs.set('status', opts.status);
  if (opts.outcome) qs.set('outcome', opts.outcome);
  if (opts.symbol?.trim()) qs.set('symbol', opts.symbol.trim());
  return qs.toString();
}
export type SortDir = 'asc' | 'desc';

export interface RulesPage {
  items: Strategy[];
  total: number;
  page: number;
  pageSize: number;
}

// ── Paper trading types ──

export interface PaperTradingHealth {
  enabled: boolean;
  // New name (per backend contract). Older deploys returned `accountReachable` —
  // both are tolerated; UI should prefer `brokerReachable` and fall back.
  brokerReachable?: boolean;
  accountReachable?: boolean;
  accountNumber?: string;
  message: string;
}

export interface PaperTradingAccount {
  accountNumber: string;
  status: string;
  equity: number;
  cash: number;
  buyingPower: number;
  daytradeCount: number;
  patternDayTrader: boolean;
  tradingBlocked: boolean;
  [key: string]: any;
}

export interface PaperTradingPosition {
  symbol: string;
  qty: number;
  side: string;
  avgEntryPrice: number;
  marketValue: number;
  costBasis: number;
  unrealizedPL: number;
  unrealizedPLPercent: number;
  currentPrice: number;
  // Enriched from our DB (null when the broker holds a position with no matching open trade — orphan).
  openedAtUtc: string | null;
  ruleId: string | null;
  ruleName: string | null;
  tradeId: string | null;
  isOrphan: boolean;
}

export interface PaperTradingOrder {
  orderId: string;
  symbol: string;
  side: string;
  qty: number;
  status: string;
  submittedAt: string | null;
  filledAt: string | null;
  filledAvgPrice: number | null;
}

export interface PaperTradingTrade {
  id: string;
  ruleId: string;
  symbol: string;
  status: string;
  entryTime: string;
  entryPrice: number;
  exitTime: string | null;
  exitPrice: number | null;
  quantity: number;
  realizedPnL: number;
  brokerEntryOrderId: string | null;
  brokerExitOrderId: string | null;
  brokerOrderError: string | null;
  // Asset-aware fields (added for the forex paper dashboard). Older callers ignore them.
  assetClass?: string;          // "Equity" | "Forex" | "Crypto"
  direction?: string;           // "Long" | "Short"
  realizedRMultiple?: number | null;
  entryReason?: string | null;
  exitReason?: string | null;
  currentStopPrice?: number;
  initialStopPrice?: number;    // original wide ATR stop at entry (drawn vs trailing stop on the chart)
  currentTakeProfitPrice?: number | null;  // live target; null when the strategy trails instead of using a TP
  initialTakeProfitPrice?: number | null;  // target set at entry (drawn on the chart)
  lastPrice?: number;           // freshest mark on an OPEN position → live unrealized P&L
  tradingEnvironment?: string;  // "Paper" | "Live" — set on SignalR-pushed trades (paper view filters to Paper)
}

/**
 * Per-user broker settings returned by GET /api/PaperTrading/settings.
 * The `apiSecretMasked` field shows last-4 chars only; the cleartext secret
 * is never returned by the server after the initial save.
 */
export interface PaperTradingSettings {
  provider: string;             // e.g. "Alpaca"
  apiKey: string;               // displayed as-is (not secret)
  apiSecretMasked: string;      // e.g. "••••abcd" — display only
  paperTradingEnabled: boolean;
  updatedAt: string;
  // Read-only mirror of the system master switch. UI uses this to render a
  // "master is off — your toggle has no effect" banner. All authenticated
  // users see this field; only SuperAdmin can change it via the dedicated
  // system-settings endpoints.
  masterEnabled: boolean;
  // Paper execution source per asset class. 'Simulated' = in-process synthetic
  // fills; 'Broker' = route to a real paper/demo account (equity → Alpaca paper,
  // forex → FXCM demo) so strategies trade under real spreads/fills.
  equityExecutionMode: PaperExecutionMode;
  forexExecutionMode: PaperExecutionMode;
}

/** Where a paper trade's orders actually go. Mirrors the backend enum. */
export type PaperExecutionMode = 'Simulated' | 'Broker';

/**
 * System-wide master kill switch for paper trading. SuperAdmin-only — the
 * GET/PUT endpoints return 403 to non-admin callers.
 */
export interface SystemPaperTradingSettings {
  masterEnabled: boolean;
  updatedAt: string;
  updatedByUserId: string | null;
}

/**
 * Body shape for PUT /api/PaperTrading/settings. All fields are optional —
 * omit (or send empty) `apiSecret` to keep the existing secret unchanged.
 */
export interface PaperTradingSettingsUpdate {
  provider?: string;
  apiKey?: string;
  apiSecret?: string;
  paperTradingEnabled?: boolean;
  equityExecutionMode?: PaperExecutionMode;
  forexExecutionMode?: PaperExecutionMode;
}

/**
 * @deprecated Old shape — kept as a type alias so legacy imports still
 * compile. Prefer PaperTradingSettings.
 */
export type PaperTradingConfig = PaperTradingSettings;

export interface PaperTradingByRule {
  ruleId: string;
  ruleName: string;
  totalTrades: number;
  winners: number;
  losers: number;
  winRate: number;
  totalPnL: number;
  avgPnL: number;
  avgHoldingMinutes: number;
  openTrades: number;
  avgRMultiple: number;
  bestTrade: number;
  worstTrade: number;
  lastTradeAt: string | null;
  totalFees: number;
}

export const automationService = new AutomationApiService();
